import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Divider, Empty, Modal, Radio, Select, Space, Tag, Typography } from "antd";
import { useTranslation } from "react-i18next";
import { formatGrams } from "functions/productData";
import { normalizeProductName } from "functions/pickRoute";
import { compactProductName, suggestLinkCandidates } from "functions/productSync";
import { SHOP_COLORS, shopLabel } from "./constants";

const { Text } = Typography;

/** The three warehouse values of a product on one line, so a wrong pick is visible up front. */
const WarehouseSummary = ({ product }) => {
  const { t } = useTranslation();
  if (!product) return null;
  return (
    <Text type="secondary" style={{ fontSize: 12 }}>
      {t("product_data.pick_weight_short")}: {formatGrams(product.pickWeightGram)} ·{" "}
      {t("product_data.volume_weight_short")}: {formatGrams(product.volumeWeightGram)}
      {!!product.pickNote && ` · ${product.pickNote}`}
    </Text>
  );
};

/**
 * Recording that a VCE product and an HVO product are the same product.
 *
 * Nothing here is automatic. The suggestions say *why* they are suggested - products that
 * already share a spot in the freezer first, then an exact name match, then a near one - and
 * whatever is chosen is shown side by side with its current warehouse values before the link
 * is made. A wrong link does not fail: it quietly puts one product's pick weight on a different
 * piece of meat, and the first person to notice is the picker standing in the freezer.
 */
/**
 * Een product komt overeen met de zoekterm als elk ingetypt woord terugkomt in de naam,
 * vergeleken zonder spaties en interpunctie. Spiegelt `filterProductRows` in functions/productData.js,
 * zodat zoeken in deze modal hetzelfde doet als zoeken in de tabel.
 */
const matchesSearch = (label, input) => {
  const needles = normalizeProductName(input).split(" ").filter(Boolean);
  if (needles.length === 0) return true;
  const haystack = compactProductName(label);
  return needles.every((needle) => haystack.includes(compactProductName(needle)));
};

const LinkProductModal = ({ open, row, products, links, pickRoute, linking, onConfirm, onClose }) => {
  const { t } = useTranslation();
  const [selectedId, setSelectedId] = useState(null);
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!open) return;
    setSelectedId(null);
    setSearch("");
  }, [open, row]);

  const suggestions = useMemo(() => {
    if (!row) return [];
    return suggestLinkCandidates({
      product: row.product,
      otherShopProducts: products,
      existingLinks: links,
      pickRoute,
    });
  }, [row, products, links, pickRoute]);

  /** Everything in the other shop that is still free, for when no suggestion is right. */
  const selectableProducts = useMemo(() => {
    if (!row) return [];
    const targetShop = row.shopSource === "VCE" ? "HVO" : "VCE";
    const taken = new Set(
      (links || [])
        .map((link) => (targetShop === "VCE" ? link.vceProductId : link.hvoProductId))
        .filter((productId) => !!productId),
    );

    return (products || [])
      .filter((product) => !!product && !!product.id && product.shopSource === targetShop)
      .filter((product) => !taken.has(product.id))
      .sort((a, b) => (a.name || "").localeCompare(b.name || "", "nl"));
  }, [row, products, links]);

  const selected = useMemo(
    () => selectableProducts.find((product) => product.id === selectedId) || null,
    [selectableProducts, selectedId],
  );

  const targetShop = !row ? null : row.shopSource === "VCE" ? "HVO" : "VCE";

  return (
    <Modal
      open={open}
      width={680}
      onCancel={onClose}
      title={t("product_data.link_title", { product: (row && row.name) || "-" })}
      footer={[
        <Button key="cancel" onClick={onClose} disabled={linking}>
          {t("cancel")}
        </Button>,
        <Button
          key="link"
          type="primary"
          loading={linking}
          disabled={!selected}
          onClick={() => onConfirm(selected)}
        >
          {t("product_data.link_confirm")}
        </Button>,
      ]}
    >
      <p style={{ color: "#6b6b6b" }}>
        {t("product_data.link_description", { shop: shopLabel(targetShop) })}
      </p>

      {suggestions.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={t("product_data.link_no_suggestions")}
        />
      ) : (
        <>
          <Text strong>{t("product_data.link_suggestions")}</Text>
          <Radio.Group
            value={selectedId}
            onChange={(event) => setSelectedId(event.target.value)}
            style={{ width: "100%", marginTop: 8 }}
          >
            <Space direction="vertical" size={8} style={{ width: "100%" }}>
              {suggestions.map((suggestion) => (
                <Radio key={suggestion.productId} value={suggestion.productId} style={{ width: "100%" }}>
                  <Space direction="vertical" size={0}>
                    <Space size={6}>
                      <strong>{suggestion.name || suggestion.productId}</strong>
                      <Tag color={suggestion.reason === "pickRoute" ? "blue" : "default"}>
                        {suggestion.reasonLabel}
                      </Tag>
                    </Space>
                    <WarehouseSummary product={suggestion.product} />
                  </Space>
                </Radio>
              ))}
            </Space>
          </Radio.Group>
        </>
      )}

      <Divider plain>{t("product_data.link_or_search")}</Divider>

      <Select
        showSearch
        style={{ width: "100%" }}
        placeholder={t("product_data.link_search_placeholder", { shop: shopLabel(targetShop) })}
        value={selectedId}
        searchValue={search}
        onSearch={setSearch}
        onChange={setSelectedId}
        filterOption={(input, option) =>
          // Zelfde matching als de tabel: per woord, en ongevoelig voor spaties en
          // interpunctie. Anders vindt "kipnuggets" hier geen "Kip nuggets 1kg" terwijl
          // de tabel op hetzelfde scherm hem wél vindt - en dit is juist de terugvaloptie
          // als de suggesties niet kloppen.
          matchesSearch(option.label, input)
        }
        options={selectableProducts.map((product) => ({
          value: product.id,
          label: product.name || product.id,
        }))}
      />

      {!!selected && !!row && (
        <div
          style={{ border: "1px solid #e6e6e6", borderRadius: 8, padding: "12px 16px", marginTop: 16 }}
        >
          <Text strong>{t("product_data.link_confirm_title")}</Text>
          <div style={{ marginTop: 8 }}>
            <Space size={6}>
              <Tag color={SHOP_COLORS[row.shopSource]}>{row.shopSource}</Tag>
              <strong>{row.name || row.id}</strong>
            </Space>
            <div>
              <WarehouseSummary product={row.product} />
            </div>
          </div>
          <div style={{ marginTop: 8 }}>
            <Space size={6}>
              <Tag color={SHOP_COLORS[targetShop]}>{targetShop}</Tag>
              <strong>{selected.name || selected.id}</strong>
            </Space>
            <div>
              <WarehouseSummary product={selected} />
            </div>
          </div>
          <Alert
            type="warning"
            showIcon
            style={{ marginTop: 12 }}
            message={t("product_data.link_warning")}
          />
        </div>
      )}
    </Modal>
  );
};

export default LinkProductModal;
